import { useState, useCallback } from 'react';
import { isAxiosError } from 'axios';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  getMonth,
  addMonths,
  parseISO,
} from 'date-fns';
import { CalendarDays, Save, Zap } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { AvailabilityCalendar } from '@/components/crew/availability-calendar';
import { useMyAvailability, useUpdateMyAvailability } from '@/hooks/use-crew';
import type { AvailabilityStatus } from '@/lib/types';
import { useOrgToday } from '@/hooks/use-settings';
import { buildAvailabilityRange } from './availability-date-range';

const STATUS_OPTIONS: { value: AvailabilityStatus; label: string; color: string }[] = [
  { value: 'available', label: 'Available', color: 'text-green-600' },
  { value: 'unavailable', label: 'Unavailable', color: 'text-red-600' },
  { value: 'tentative', label: 'Tentative', color: 'text-yellow-600' },
];

const _STATUS_CYCLE: AvailabilityStatus[] = ['available', 'unavailable', 'tentative'];

interface AvailabilityPreset {
  id: string;
  label: string;
  description: string;
  /** Generate updates for the given month range. */
  generate: (monthStart: Date, monthEnd: Date) => { date: string; status: AvailabilityStatus }[];
}

const AVAILABILITY_PRESETS: AvailabilityPreset[] = [
  {
    id: 'available-local',
    label: 'Available Locally — Weekdays',
    description: 'Mark all weekdays as available, weekends as unavailable',
    generate: (start, end) => {
      return eachDayOfInterval({ start, end }).map((day) => ({
        date: format(day, 'yyyy-MM-dd'),
        status: (getDay(day) === 0 || getDay(day) === 6 ? 'unavailable' : 'available') as AvailabilityStatus,
      }));
    },
  },
  {
    id: 'available-always',
    label: 'Available Always',
    description: 'Mark every day in the current month as available',
    generate: (start, end) => {
      return eachDayOfInterval({ start, end }).map((day) => ({
        date: format(day, 'yyyy-MM-dd'),
        status: 'available' as AvailabilityStatus,
      }));
    },
  },
  {
    id: 'unavailable-month',
    label: 'Unavailable — Entire Month',
    description: 'Mark every day in the current month as unavailable',
    generate: (start, end) => {
      return eachDayOfInterval({ start, end }).map((day) => ({
        date: format(day, 'yyyy-MM-dd'),
        status: 'unavailable' as AvailabilityStatus,
      }));
    },
  },
  {
    id: 'travel-except-dec',
    label: 'Available to Travel — Except December',
    description: 'Available all months; unavailable in December (fills 3 months ahead)',
    generate: (start) => {
      const updates: { date: string; status: AvailabilityStatus }[] = [];
      for (let m = 0; m < 3; m++) {
        const ms = startOfMonth(addMonths(start, m));
        const me = endOfMonth(ms);
        const isDecember = getMonth(ms) === 11;
        eachDayOfInterval({ start: ms, end: me }).forEach((day) => {
          updates.push({
            date: format(day, 'yyyy-MM-dd'),
            status: isDecember ? 'unavailable' : 'available',
          });
        });
      }
      return updates;
    },
  },
  {
    id: 'tentative-month',
    label: 'Tentative — Entire Month',
    description: 'Mark every day in the current month as tentative',
    generate: (start, end) => {
      return eachDayOfInterval({ start, end }).map((day) => ({
        date: format(day, 'yyyy-MM-dd'),
        status: 'tentative' as AvailabilityStatus,
      }));
    },
  },
];

export function MyAvailabilityPage() {
  const orgToday = useOrgToday();
  const [currentMonth, setCurrentMonth] = useState(() => parseISO(orgToday));
  const [selectedStatus, setSelectedStatus] = useState<AvailabilityStatus>('available');
  const [pendingUpdates, setPendingUpdates] = useState<
    { date: string; status: AvailabilityStatus }[]
  >([]);

  const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const { data: entries = [], isLoading, error } = useMyAvailability({
    date_from: monthStart,
    date_to: monthEnd,
  });
  const updateMutation = useUpdateMyAvailability();
  const hasNoTechProfile = isAxiosError(error) && error.response?.status === 404;

  const handleDateClick = useCallback(
    (date: string, _currentStatus: AvailabilityStatus | null) => {
      // Cycle through statuses or set selected status
      const nextStatus = selectedStatus;
      setPendingUpdates((prev) => {
        const existing = prev.findIndex((u) => u.date === date);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { date, status: nextStatus };
          return updated;
        }
        return [...prev, { date, status: nextStatus }];
      });
    },
    [selectedStatus]
  );

  const handleDateRangeSelect = useCallback(
    (startDate: string, endDate: string) => {
      const updates = buildAvailabilityRange(startDate, endDate, selectedStatus);
      setPendingUpdates((prev) => {
        const dateSet = new Set(updates.map((u) => u.date));
        const filtered = prev.filter((u) => !dateSet.has(u.date));
        return [...filtered, ...updates];
      });
    },
    [selectedStatus]
  );

  const handleSave = () => {
    if (pendingUpdates.length === 0) return;
    updateMutation.mutate(pendingUpdates, {
      onSuccess: () => {
        setPendingUpdates([]);
      },
    });
  };

  // Merge server entries with pending updates
  const mergedEntries = [...entries];
  pendingUpdates.forEach((update) => {
    const idx = mergedEntries.findIndex((e) => e.date === update.date);
    if (idx >= 0) {
      mergedEntries[idx] = { ...mergedEntries[idx], status: update.status };
    } else {
      mergedEntries.push({
        id: -1,
        tech: 0,
        tech_name: '',
        organization: 0,
        date: update.date,
        status: update.status,
        notes: '',
      });
    }
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Availability</h1>
          </div>
          <p className="text-gray-600 mt-1">
            Click or drag on dates to set your availability, then save.
          </p>
        </div>
      </div>

      {hasNoTechProfile ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Technician profile required</CardTitle>
            <CardDescription>
              Your account does not have a technician profile. Availability can be managed after
              an organization administrator creates one for you.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* Controls */}
          <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Set status:</span>
              <Select
                value={selectedStatus}
                onValueChange={(v) => setSelectedStatus(v as AvailabilityStatus)}
              >
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className={opt.color}>{opt.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {pendingUpdates.length > 0 && (
                <span className="text-sm text-amber-600">
                  {pendingUpdates.length} unsaved change{pendingUpdates.length > 1 ? 's' : ''}
                </span>
              )}
              <Button
                onClick={handleSave}
                disabled={pendingUpdates.length === 0 || updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              {pendingUpdates.length > 0 && (
                <Button variant="ghost" onClick={() => setPendingUpdates([])}>
                  Discard
                </Button>
              )}
            </div>
          </div>
        </CardContent>
          </Card>

          {/* Quick Presets */}
          <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Quick Presets
          </CardTitle>
          <CardDescription>
            Apply a preset to quickly fill the current month. Changes are staged — save when ready.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {AVAILABILITY_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                variant="outline"
                size="sm"
                title={preset.description}
                onClick={() => {
                  const mStart = startOfMonth(currentMonth);
                  const mEnd = endOfMonth(currentMonth);
                  const updates = preset.generate(mStart, mEnd);
                  setPendingUpdates((prev) => {
                    const dateSet = new Set(updates.map((u) => u.date));
                    const filtered = prev.filter((u) => !dateSet.has(u.date));
                    return [...filtered, ...updates];
                  });
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardContent>
          </Card>

          {/* Calendar */}
          <Card>
        <CardHeader>
          <CardTitle className="text-base">Availability Calendar</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
          ) : (
            <AvailabilityCalendar
              entries={mergedEntries}
              month={currentMonth}
              onDateClick={handleDateClick}
              onDateRangeSelect={handleDateRangeSelect}
              onMonthChange={setCurrentMonth}
              isEditable
              className="max-w-lg mx-auto"
            />
          )}
        </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
